'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { Progress } from '../ui/progress'
import { 
  MessageCircle, 
  Wand2,
  Copy,
  CheckCircle,
  Save,
  Download,
  Plus,
  Trash2,
  Users,
  Target,
  BookOpen,
  Lightbulb
} from 'lucide-react'
import { Brand } from '../../utils/mockData'

interface VoiceToneGuideProps {
  brand: Brand
}

interface PersonalityTrait {
  name: string
  description: string
  examples: string[]
}

interface ToneScenario {
  scenario: string
  tone: string
  example: string
  doExample: string
  dontExample: string
}

interface VoiceData {
  brandPersonality: {
    traits: string[]
    archetype: string
    description: string
  }
  toneAttributes: {
    formal: number // 0-100 scale
    friendly: number
    authoritative: number
    playful: number
    empathetic: number
    confident: number
  }
  communicationGuidelines: {
    dosList: string[]
    dontsList: string[]
    keyMessages: string[]
  }
  toneScenarios: ToneScenario[]
  writingExamples: {
    headlines: string[]
    taglines: string[]
    socialPosts: string[]
    emailSubjects: string[]
  }
}

const BRAND_ARCHETYPES = [
  { name: 'The Innocent', description: 'Optimistic, honest, and pure' },
  { name: 'The Explorer', description: 'Adventurous, pioneering, and free' },
  { name: 'The Sage', description: 'Wise, intelligent, and thoughtful' },
  { name: 'The Hero', description: 'Courageous, determined, and honorable' },
  { name: 'The Outlaw', description: 'Rebellious, revolutionary, and wild' },
  { name: 'The Magician', description: 'Visionary, inventive, and charismatic' },
  { name: 'The Regular Guy/Girl', description: 'Down-to-earth, authentic, and friendly' },
  { name: 'The Lover', description: 'Passionate, committed, and intimate' },
  { name: 'The Jester', description: 'Playful, humorous, and light-hearted' },
  { name: 'The Caregiver', description: 'Caring, generous, and compassionate' },
  { name: 'The Ruler', description: 'Responsible, authoritative, and organized' },
  { name: 'The Creator', description: 'Creative, imaginative, and artistic' }
]

const PERSONALITY_TRAITS = [
  'Professional', 'Friendly', 'Innovative', 'Reliable', 'Approachable',
  'Expert', 'Passionate', 'Trustworthy', 'Creative', 'Authentic',
  'Confident', 'Empathetic', 'Bold', 'Sophisticated', 'Energetic',
  'Humble', 'Inspiring', 'Direct', 'Warm', 'Thoughtful'
]

const DEFAULT_SCENARIOS: ToneScenario[] = [
  {
    scenario: 'Customer Service Response',
    tone: 'Empathetic and Solution-focused',
    example: 'We understand your frustration and are here to help resolve this quickly.',
    doExample: 'Acknowledge the issue, show empathy, provide clear next steps',
    dontExample: 'Use dismissive language or blame the customer'
  },
  {
    scenario: 'Marketing Campaign',
    tone: 'Exciting and Benefits-focused',
    example: 'Discover how our innovative solution can transform your workflow.',
    doExample: 'Focus on benefits, use active voice, create excitement',
    dontExample: 'Use jargon or focus only on features'
  },
  {
    scenario: 'Educational Content',
    tone: 'Informative and Accessible',
    example: 'Let\'s break down this complex topic into simple, actionable steps.',
    doExample: 'Use clear explanations, provide examples, be encouraging',
    dontExample: 'Overwhelm with technical details or assume knowledge'
  }
]

export function VoiceToneGuide({ brand }: VoiceToneGuideProps) {
  const [voiceData, setVoiceData] = useState<VoiceData>({
    brandPersonality: {
      traits: [],
      archetype: '',
      description: ''
    },
    toneAttributes: {
      formal: 50,
      friendly: 70,
      authoritative: 60,
      playful: 30,
      empathetic: 80,
      confident: 70
    },
    communicationGuidelines: {
      dosList: [],
      dontsList: [],
      keyMessages: []
    },
    toneScenarios: [...DEFAULT_SCENARIOS],
    writingExamples: {
      headlines: [],
      taglines: [],
      socialPosts: [],
      emailSubjects: []
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [newDo, setNewDo] = useState('')
  const [newDont, setNewDont] = useState('')
  const [newMessage, setNewMessage] = useState('')

  useEffect(() => {
    loadVoiceData()
  }, [brand.id])

  const loadVoiceData = async () => {
    setIsLoading(true)
    try {
      const savedData = localStorage.getItem(`voiceTone_${brand.id}`)
      if (savedData) {
        setVoiceData(JSON.parse(savedData))
      }
    } catch (error) {
      console.error('Error loading voice data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveVoiceData = async () => {
    setIsSaving(true)
    try {
      localStorage.setItem(`voiceTone_${brand.id}`, JSON.stringify(voiceData))
      setLastSaved(new Date())
    } catch (error) {
      console.error('Error saving voice data:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const updateToneAttribute = (attribute: keyof VoiceData['toneAttributes'], value: number) => {
    setVoiceData(prev => ({
      ...prev,
      toneAttributes: {
        ...prev.toneAttributes,
        [attribute]: value
      }
    }))
  }

  const toggleTrait = (trait: string) => {
    setVoiceData(prev => ({
      ...prev,
      brandPersonality: {
        ...prev.brandPersonality,
        traits: prev.brandPersonality.traits.includes(trait)
          ? prev.brandPersonality.traits.filter(t => t !== trait)
          : [...prev.brandPersonality.traits, trait]
      }
    }))
  }

  const addToList = (listType: 'dosList' | 'dontsList' | 'keyMessages', value: string, setValue: (val: string) => void) => {
    if (!value.trim()) return
    
    setVoiceData(prev => ({
      ...prev,
      communicationGuidelines: {
        ...prev.communicationGuidelines,
        [listType]: [...prev.communicationGuidelines[listType], value.trim()]
      }
    }))
    setValue('')
  }

  const removeFromList = (listType: 'dosList' | 'dontsList' | 'keyMessages', index: number) => {
    setVoiceData(prev => ({
      ...prev,
      communicationGuidelines: {
        ...prev.communicationGuidelines,
        [listType]: prev.communicationGuidelines[listType].filter((_, i) => i !== index)
      }
    }))
  }

  const generateAIVoice = async () => {
    setIsLoading(true)
    try {
      // Mock AI voice generation based on brand industry and personality
      const industryVoiceMap: Record<string, Partial<VoiceData>> = {
        'Technology': {
          brandPersonality: {
            traits: ['Innovative', 'Professional', 'Reliable', 'Expert'],
            archetype: 'The Magician',
            description: 'A forward-thinking technology brand that speaks with authority while remaining approachable and solution-focused.'
          },
          toneAttributes: {
            formal: 60,
            friendly: 70,
            authoritative: 80,
            playful: 20,
            empathetic: 60,
            confident: 85
          }
        },
        'Healthcare': {
          brandPersonality: {
            traits: ['Caring', 'Trustworthy', 'Expert', 'Empathetic'],
            archetype: 'The Caregiver',
            description: 'A compassionate healthcare brand that communicates with warmth, expertise, and genuine care for patient wellbeing.'
          },
          toneAttributes: {
            formal: 70,
            friendly: 80,
            authoritative: 75,
            playful: 10,
            empathetic: 95,
            confident: 70
          }
        }
      }

      const industry = brand.metadata?.industry || 'Technology'
      const suggestion = industryVoiceMap[industry] || industryVoiceMap['Technology']

      setVoiceData(prev => ({
        ...prev,
        ...suggestion,
        communicationGuidelines: {
          dosList: [
            'Speak directly to your audience\'s needs',
            'Use clear, jargon-free language',
            'Show empathy and understanding',
            'Back up claims with evidence'
          ],
          dontsList: [
            'Use overly technical language',
            'Make promises you can\'t keep',
            'Ignore customer concerns',
            'Use pushy sales tactics'
          ],
          keyMessages: [
            `${brand.name} delivers innovative solutions`,
            'We prioritize customer success',
            'Quality and reliability are our foundation'
          ]
        }
      }))
    } catch (error) {
      console.error('Error generating AI voice:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const exportVoiceGuide = () => {
    const voiceGuideData = {
      brand: brand.name,
      voice: voiceData,
      exported: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(voiceGuideData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${brand.name.toLowerCase().replace(/\s+/g, '-')}-voice-tone-guide.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (isLoading && !voiceData.brandPersonality.traits.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-slate-600">Loading voice & tone guide...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Voice & Tone Guide</h2>
          <p className="text-slate-600">Define how your brand communicates and sounds</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
          <Button 
            variant="outline"
            onClick={exportVoiceGuide}
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            onClick={saveVoiceData}
            disabled={isSaving}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Guide'}
          </Button>
        </div>
      </div>

      {/* AI Voice Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            AI Voice Analysis
          </CardTitle>
          <CardDescription>
            Get AI-powered voice and tone recommendations based on your brand
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Button 
              onClick={generateAIVoice}
              disabled={isLoading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {isLoading ? 'Analyzing...' : 'Generate AI Voice Profile'}
            </Button>
            <span className="text-sm text-slate-600">
              Based on {brand.metadata?.industry || 'Technology'} industry
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Brand Personality */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Brand Personality
            </CardTitle>
            <CardDescription>
              Define your brand's core personality traits and archetype
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Brand Archetype</Label>
              <select 
                className="w-full p-2 border rounded-md"
                value={voiceData.brandPersonality.archetype}
                onChange={(e) => setVoiceData(prev => ({
                  ...prev,
                  brandPersonality: { ...prev.brandPersonality, archetype: e.target.value }
                }))}
              >
                <option value="">Select an archetype...</option>
                {BRAND_ARCHETYPES.map(archetype => (
                  <option key={archetype.name} value={archetype.name}>
                    {archetype.name} - {archetype.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Personality Traits</Label>
              <div className="flex flex-wrap gap-2">
                {PERSONALITY_TRAITS.map(trait => (
                  <Button
                    key={trait}
                    size="sm"
                    variant={voiceData.brandPersonality.traits.includes(trait) ? "default" : "outline"}
                    onClick={() => toggleTrait(trait)}
                    className="h-8 px-3 text-xs"
                  >
                    {trait}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Brand Personality Description</Label>
              <Textarea
                value={voiceData.brandPersonality.description}
                onChange={(e) => setVoiceData(prev => ({
                  ...prev,
                  brandPersonality: { ...prev.brandPersonality, description: e.target.value }
                }))}
                placeholder="Describe your brand's personality in a few sentences..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Tone Attributes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Tone Attributes
            </CardTitle>
            <CardDescription>
              Adjust the balance of different tone characteristics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(voiceData.toneAttributes).map(([attribute, value]) => (
              <div key={attribute} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="capitalize">{attribute}</Label>
                  <span className="text-sm text-slate-600">{value}%</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-slate-500 w-16">
                    {attribute === 'formal' ? 'Casual' :
                     attribute === 'friendly' ? 'Reserved' :
                     attribute === 'authoritative' ? 'Humble' :
                     attribute === 'playful' ? 'Serious' :
                     attribute === 'empathetic' ? 'Detached' :
                     attribute === 'confident' ? 'Modest' : 'Low'}
                  </span>
                  <div className="flex-1">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={value}
                      onChange={(e) => updateToneAttribute(attribute as keyof VoiceData['toneAttributes'], parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-16 text-right">
                    {attribute === 'formal' ? 'Formal' :
                     attribute === 'friendly' ? 'Friendly' :
                     attribute === 'authoritative' ? 'Authoritative' :
                     attribute === 'playful' ? 'Playful' :
                     attribute === 'empathetic' ? 'Empathetic' :
                     attribute === 'confident' ? 'Confident' : 'High'}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Communication Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Communication Guidelines
          </CardTitle>
          <CardDescription>
            Define what to do and what to avoid in your brand communications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Do's */}
            <div className="space-y-3">
              <Label className="text-green-700 font-medium">✓ Do</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="Add a communication guideline..."
                  value={newDo}
                  onChange={(e) => setNewDo(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToList('dosList', newDo, setNewDo)}
                />
                <Button 
                  size="sm" 
                  onClick={() => addToList('dosList', newDo, setNewDo)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {voiceData.communicationGuidelines.dosList.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                    <span className="text-green-800">{item}</span>
                    <button
                      onClick={() => removeFromList('dosList', index)}
                      className="text-green-600 hover:text-green-800"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Don'ts */}
            <div className="space-y-3">
              <Label className="text-red-700 font-medium">✗ Don't</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="Add something to avoid..."
                  value={newDont}
                  onChange={(e) => setNewDont(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToList('dontsList', newDont, setNewDont)}
                />
                <Button 
                  size="sm" 
                  onClick={() => addToList('dontsList', newDont, setNewDont)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {voiceData.communicationGuidelines.dontsList.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded text-sm">
                    <span className="text-red-800">{item}</span>
                    <button
                      onClick={() => removeFromList('dontsList', index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Key Messages */}
          <div className="space-y-3">
            <Label className="font-medium">Key Brand Messages</Label>
            <div className="flex space-x-2">
              <Input
                placeholder="Add a key brand message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addToList('keyMessages', newMessage, setNewMessage)}
              />
              <Button 
                size="sm" 
                onClick={() => addToList('keyMessages', newMessage, setNewMessage)}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {voiceData.communicationGuidelines.keyMessages.map((message, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded">
                  <span className="text-blue-800 text-sm">{message}</span>
                  <button
                    onClick={() => removeFromList('keyMessages', index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tone Scenarios */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Tone by Scenario
          </CardTitle>
          <CardDescription>
            Examples of how to adjust tone for different communication scenarios
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {voiceData.toneScenarios.map((scenario, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-medium">{scenario.scenario}</Label>
                    <p className="text-sm text-slate-600 mb-2">{scenario.tone}</p>
                    <div className="p-3 bg-slate-50 rounded text-sm italic">
                      "{scenario.example}"
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-green-700 text-xs font-medium">✓ DO</Label>
                      <p className="text-sm text-green-800">{scenario.doExample}</p>
                    </div>
                    <div>
                      <Label className="text-red-700 text-xs font-medium">✗ DON'T</Label>
                      <p className="text-sm text-red-800">{scenario.dontExample}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}